import React, { FC, memo, useMemo, useState } from 'react';
import { Contract } from 'entities/Contract';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Payment } from 'entities/Payment';
import { PaymentCard } from './PaymentCard';
import { Invoice } from 'entities/Invoice';
import { PaymentCreditNoteType } from 'entities/CreditNote';
import { WarnTag } from 'shared/ui/WarnTag';

interface PaymentTabProps {
  payments: Payment[];
  contract: Contract;
  depositInvoice?: Invoice;
}

export const PaymentTab: FC<PaymentTabProps> = memo((props) => {
  const { payments, contract, depositInvoice } = props;

  const { t } = useAppTranslation('contracts');

  const [showReversalCreditPayments, setShowReversalCreditPayments] = useState(false);

  const hasHiddenCancelledPayments = useMemo(
    () => payments.some((payment) => payment.creditNote?.creditNoteType === PaymentCreditNoteType.PAYMENT_REVERSAL_CREDIT),
    [payments],
  );

  const visiblePayments = useMemo(() => {
    if (showReversalCreditPayments) {
      return payments;
    }
    return payments.filter((payment) => payment.creditNote?.creditNoteType !== PaymentCreditNoteType.PAYMENT_REVERSAL_CREDIT);
  }, [payments, showReversalCreditPayments]);

  const hiddenCancelledPaymentsWarn = hasHiddenCancelledPayments ? (
    <WarnTag textSize="medium">
      {t('This contract contains hidden cancelled payments')}.{' '}
      <span
        onClick={() => {
          setShowReversalCreditPayments((prev) => !prev);
        }}
        className="font-medium cursor-pointer underline"
      >
        ({showReversalCreditPayments ? t('Hide') : t('Show')})
      </span>
    </WarnTag>
  ) : null;

  return (
    <div>
      {visiblePayments.length ? (
        <>
          {hiddenCancelledPaymentsWarn && <div className="pt-7">{hiddenCancelledPaymentsWarn}</div>}
          <div className="font-semibold text-lg pt-7 pb-4">{t('History')}</div>
          <div className="space-y-3">
            {visiblePayments.map((payment) => (
              <PaymentCard
                key={payment.paymentId}
                payment={payment}
                deposit={contract.deposit}
                user={contract.user}
                isClosedContract={Boolean(contract.leaveDate)}
                isDepositPayment={depositInvoice?.invoiceId === payment.invoice.invoiceId}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="pt-7 pb-4 text-center">
          {hiddenCancelledPaymentsWarn && <div className="mb-3">{hiddenCancelledPaymentsWarn}</div>}
          <div className="font-semibold text-lg">{t('No payments yet')}</div>
        </div>
      )}
    </div>
  );
});
